import React, { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import ChatWidget from '../components/chatbot/ChatWidget';
import ScrollingGallery from '../components/ScrollingGallery';
import MahoragaPixelWheel from '../components/MahoragaPixelWheel';
import ProjectMiniatureMedia from '../components/ProjectMiniatureMedia';
import { getShowcaseProjectById } from '../components/GalleryData';
import {
  PROJECTS,
  PROCESS_STEPS,
  PROCESS_STEPS_EN,
  SERVICES,
  SERVICES_EN,
  FAQS,
  FAQS_EN,
  Project,
  ProjectCategory
} from '../constants';
import { useTheme } from '../theme';
import { useLanguage } from '../lib/language';

// Helper to get text styles based on theme
const getTextStyles = (theme: { mood: string; text: { primary: string } }) => {
  const isLight = theme.mood === 'light';
  // Use CSS variables for dynamic color, with fallback
  return {
    color: 'var(--theme-text-primary, ' + theme.text.primary + ')',
    textShadow: isLight
      ? '0 1px 1px rgba(17,24,39,0.08)'
      : '0 2px 4px rgba(0,0,0,0.5), 0 4px 12px rgba(0,0,0,0.3)',
  };
};

const getTextShadowLight = (theme: { mood: string }) => {
  const isLight = theme.mood === 'light';
  return isLight
    ? '0 1px 1px rgba(17,24,39,0.08)'
    : '0 1px 3px rgba(0,0,0,0.4), 0 2px 8px rgba(0,0,0,0.2)';
};

// Helper to get background colors (inverted for light themes)
const getBgColor = (theme: { mood: string }, opacity: number = 0.4) => {
  const isLight = theme.mood === 'light';
  const tunedOpacity = Math.max(0, Math.min(1, opacity * 0.82));
  return isLight
    ? `rgba(255, 255, 255, ${tunedOpacity})`
    : `rgba(0, 0, 0, ${tunedOpacity})`;
};

// Helper to get border colors (inverted for light themes)
const getBorderColor = (theme: { mood: string }, opacity: number = 0.1) => {
  const isLight = theme.mood === 'light';
  return isLight
    ? `rgba(0, 0, 0, ${opacity})`
    : `rgba(255, 255, 255, ${opacity})`;
};

// ============================================
// PRODUCT TYPES SECTION
// ============================================
const Services: React.FC = () => {
  const { theme } = useTheme();
  const { language } = useLanguage();
  const textStyles = getTextStyles(theme);
  const [hoveredType, setHoveredType] = useState<string | null>(null);
  const [selectedProjectId, setSelectedProjectId] = useState<string | null>(null);
  const previewTimerRef = useRef<number | null>(null);
  const smoothEase = [0.16, 1, 0.3, 1] as const;

  const productTypes = [
    { id: 'products', label: language === 'es' ? 'Productos' : 'Products', categories: ['product'], color: '#F4A027' },
    { id: 'marketplaces', label: 'Marketplaces', categories: ['marketplace'], color: '#B6C7B1' },
    { id: 'websites', label: language === 'es' ? 'Webs' : 'Websites', categories: ['website', 'landing'], color: '#D9C7A5' },
    { id: 'ai', label: 'AI', categories: ['ai'], color: '#EDE9E0' },
  ];

  const activeType = productTypes.find((type) => type.id === hoveredType) ?? null;
  const activeProjects = activeType ? PROJECTS.filter((project) => activeType.categories.includes(project.category)) : [];
  const selectedProject = selectedProjectId ? activeProjects.find((project) => project.id === selectedProjectId) ?? null : null;
  const selectedShowcase = selectedProject ? getShowcaseProjectById(selectedProject.id) : null;
  const selectedImages = selectedProject
    ? selectedShowcase?.gallery?.length
      ? selectedShowcase.gallery
      : [selectedProject.image]
    : [];

  const clearPreviewTimer = () => {
    if (previewTimerRef.current) {
      window.clearTimeout(previewTimerRef.current);
      previewTimerRef.current = null;
    }
  };

  const scheduleProjectPreview = (projectId: string) => {
    clearPreviewTimer();
    previewTimerRef.current = window.setTimeout(() => setSelectedProjectId(projectId), 650);
  };

  const closeArea = () => {
    clearPreviewTimer();
    setSelectedProjectId(null);
    setHoveredType(null);
  };

  return (
    <section id="services" className="relative py-20 sm:py-28 border-t" style={{ borderColor: getBorderColor(theme, 0.1) }}>
      <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-16">
        <div className="mb-8 sm:mb-10 max-w-3xl">
          <span className="text-[11px] uppercase tracking-[0.28em] mb-4 block" style={{ color: 'var(--theme-text-muted, ' + theme.text.muted + ')' }}>
            {language === 'es' ? '4 áreas' : '4 areas'}
          </span>
          <h2 className="font-pixel text-3xl sm:text-4xl md:text-5xl font-black tracking-tight mb-4 leading-tight" style={textStyles}>
            {language === 'es' ? 'Lo que construimos.' : 'What we build.'}
          </h2>
        </div>

        <div className="relative min-h-[540px] sm:min-h-[620px] rounded-3xl overflow-hidden">
          <div className="absolute inset-0 grid grid-cols-1 md:grid-cols-2">
            {productTypes.map((type, idx) => {
              const active = hoveredType === type.id;
              return (
                <motion.button
                  key={type.id}
                  type="button"
                  onClick={() => {
                    clearPreviewTimer();
                    setSelectedProjectId(null);
                    setHoveredType(type.id);
                  }}
                  className="relative p-6 sm:p-8 border outline-none overflow-hidden text-left touch-manipulation"
                  style={{ borderColor: getBorderColor(theme, active ? 0.2 : 0.08), backgroundColor: 'transparent' }}
                  animate={{ opacity: hoveredType && !active ? 0.24 : 1 }}
                  transition={{ duration: 0.18, ease: smoothEase }}
                >
                  <span className="absolute top-6 left-6 sm:top-8 sm:left-8 font-mono text-[10px] uppercase tracking-[0.22em]" style={{ color: active ? type.color : 'var(--theme-text-muted, ' + theme.text.muted + ')' }}>
                    0{idx + 1}
                  </span>
                  <div className="h-full flex items-center justify-center text-center">
                    <motion.span
                      className="font-pixel text-[clamp(3rem,9vw,8rem)] font-black tracking-[-0.08em] leading-none select-none will-change-transform"
                      animate={{ scale: active ? 1.035 : 1 }}
                      transition={{ duration: 0.2, ease: smoothEase }}
                      style={{
                        color: 'transparent',
                        WebkitTextFillColor: 'transparent',
                        WebkitTextStroke: active ? `1px ${type.color}` : `1px ${theme.mood === 'light' ? 'rgba(17,24,39,0.46)' : 'rgba(255,255,255,0.42)'}`,
                        backgroundImage: active ? `linear-gradient(135deg, rgba(255,255,255,0.95), ${type.color}, rgba(255,255,255,0.28))` : `linear-gradient(135deg, rgba(255,255,255,0.62), ${theme.mood === 'light' ? 'rgba(17,24,39,0.34)' : 'rgba(255,255,255,0.18)'})`,
                        WebkitBackgroundClip: 'text',
                        backgroundClip: 'text',
                        textShadow: active ? `0 0 22px ${type.color}44, 0 18px 70px rgba(255,255,255,0.12)` : theme.mood === 'light' ? '0 12px 40px rgba(17,24,39,0.10)' : '0 12px 50px rgba(255,255,255,0.08)',
                      }}
                    >
                      {type.label}
                    </motion.span>
                  </div>
                </motion.button>
              );
            })}
          </div>

          <AnimatePresence mode="wait">
            {activeType && (
              <motion.div
                key={activeType.id}
                className="absolute inset-0 z-10 overflow-y-auto overscroll-contain p-5 sm:p-8 lg:p-10"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.2, ease: smoothEase }}
                style={{
                  background: theme.mood === 'light'
                    ? 'linear-gradient(180deg, rgba(255,255,255,0.46), rgba(255,255,255,0.86))'
                    : 'linear-gradient(180deg, rgba(5,5,5,0.32), rgba(5,5,5,0.88))',
                  backdropFilter: 'blur(20px) saturate(1.25)',
                  WebkitBackdropFilter: 'blur(20px) saturate(1.25)',
                }}
              >
                <button
                  type="button"
                  onClick={closeArea}
                  className="absolute right-5 top-5 z-40 flex h-10 w-10 items-center justify-center rounded-full border transition-opacity hover:opacity-70"
                  style={{
                    borderColor: getBorderColor(theme, 0.18),
                    color: 'var(--theme-text-primary, ' + theme.text.primary + ')',
                    backgroundColor: theme.mood === 'light' ? 'rgba(255,255,255,0.45)' : 'rgba(255,255,255,0.08)',
                  }}
                  aria-label="Close overlay"
                >
                  <span className="material-symbols-outlined text-xl">close</span>
                </button>

                {selectedProject && (
                  <button
                    type="button"
                    onClick={() => {
                      clearPreviewTimer();
                      setSelectedProjectId(null);
                    }}
                    className="absolute left-5 top-5 z-40 flex h-10 w-10 items-center justify-center rounded-full border transition-opacity hover:opacity-70"
                    style={{
                      borderColor: getBorderColor(theme, 0.18),
                      color: 'var(--theme-text-primary, ' + theme.text.primary + ')',
                      backgroundColor: theme.mood === 'light' ? 'rgba(255,255,255,0.45)' : 'rgba(255,255,255,0.08)',
                    }}
                    aria-label="Back to project list"
                  >
                    <span className="material-symbols-outlined text-xl">arrow_back</span>
                  </button>
                )}

                <AnimatePresence mode="wait" initial={false}>
                {!selectedProject ? (
                  <motion.div
                    key="area-grid"
                    className="relative z-10 flex min-h-full flex-col"
                    initial={{ opacity: 0, y: 10, filter: 'blur(6px)' }}
                    animate={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
                    exit={{ opacity: 0, y: -8, filter: 'blur(6px)' }}
                    transition={{ duration: 0.2, ease: smoothEase }}
                  >
                    <div className="mb-6 sm:mb-8 pr-12">
                      <h3
                        className="font-pixel text-[clamp(2.8rem,7vw,6.5rem)] font-black tracking-[-0.08em] leading-none"
                        style={{
                          color: 'transparent',
                          WebkitTextFillColor: 'transparent',
                          WebkitTextStroke: `1px ${activeType.color}`,
                          backgroundImage: `linear-gradient(135deg, rgba(255,255,255,0.95), ${activeType.color}, rgba(255,255,255,0.22))`,
                          WebkitBackgroundClip: 'text',
                          backgroundClip: 'text',
                          textShadow: `0 0 24px ${activeType.color}44`,
                        }}
                      >
                        {activeType.label}
                      </h3>
                    </div>

                    <div className="grid flex-1 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 pb-2">
                      {activeProjects.map((project, idx) => (
                        <motion.div
                          key={project.id}
                          initial={{ opacity: 0, y: 14, filter: 'blur(8px)' }}
                          animate={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
                          transition={{ duration: 0.22, ease: smoothEase, delay: idx * 0.018 }}
                          className="min-h-0"
                        >
                          <div
                            role="button"
                            tabIndex={0}
                            onClick={() => {
                              clearPreviewTimer();
                              setSelectedProjectId(project.id);
                            }}
                            onKeyDown={(event) => {
                              if (event.key === 'Enter' || event.key === ' ') {
                                event.preventDefault();
                                clearPreviewTimer();
                                setSelectedProjectId(project.id);
                              }
                            }}
                            className="group/project relative block h-full min-h-[130px] sm:min-h-[150px] overflow-hidden rounded-2xl border cursor-pointer outline-none bg-cover bg-center"
                            style={{
                              borderColor: getBorderColor(theme, 0.16),
                              backgroundImage: `url(${project.image}?v=real-shots)`,
                            }}
                          >
                            <img src={`${project.image}?v=real-shots`} alt={project.title} className="absolute inset-0 h-full w-full object-cover transition-transform duration-500 ease-out group-hover/project:scale-105" loading={idx === 0 ? 'eager' : 'lazy'} />
                            <div className="absolute inset-0 bg-gradient-to-t from-black/78 via-black/18 to-transparent" />
                            <div className="absolute inset-0 opacity-0 transition-opacity duration-300 group-hover/project:opacity-100" style={{ backgroundColor: activeType.color, mixBlendMode: 'overlay' }} />
                            <div className="absolute left-4 right-4 bottom-4 flex items-end justify-between gap-3">
                              <div className="min-w-0">
                                <p className="text-white text-base sm:text-lg font-semibold leading-tight truncate">{project.title}</p>
                                <p className="mt-1 text-white/70 text-xs line-clamp-1">{language === 'en' ? project.subtitleEn : project.subtitle}</p>
                              </div>
                              <span className="material-symbols-outlined text-lg shrink-0 text-white/80 transition-transform group-hover/project:translate-x-1">open_in_full</span>
                            </div>
                          </div>
                        </motion.div>
                      ))}
                    </div>
                  </motion.div>
                ) : (
                  <motion.div
                    key="project-view"
                    className="relative z-10 grid min-h-full grid-cols-1 lg:grid-cols-[1.55fr_0.65fr] gap-5 sm:gap-7 pt-12 sm:pt-0"
                    initial={{ opacity: 0, y: 10, filter: 'blur(6px)' }}
                    animate={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
                    exit={{ opacity: 0, y: -8, filter: 'blur(6px)' }}
                    transition={{ duration: 0.22, ease: smoothEase }}
                  >
                    <div className="grid min-h-0 grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                      {selectedImages.map((image, imageIdx) => (
                        <motion.div
                          key={`${selectedProject.id}-${image}-${imageIdx}`}
                          initial={{ opacity: 0, y: 14, filter: 'blur(8px)' }}
                          animate={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
                          transition={{ duration: 0.24, ease: smoothEase, delay: imageIdx * 0.025 }}
                          className={imageIdx === 0 ? 'h-[260px] sm:col-span-2 sm:h-auto sm:min-h-[260px]' : 'h-[170px] sm:h-auto sm:min-h-[170px]'}
                        >
                          <div
                            className="relative h-full overflow-hidden rounded-3xl border bg-cover bg-center"
                            style={{
                              borderColor: getBorderColor(theme, 0.16),
                              backgroundImage: `url(${image}?v=real-shots)`,
                            }}
                          >
                            <img src={`${image}?v=real-shots`} alt={`${selectedProject.title} screenshot ${imageIdx + 1}`} className="absolute inset-0 h-full w-full object-cover" loading={imageIdx === 0 ? 'eager' : 'lazy'} />
                            <div className="absolute inset-0 bg-gradient-to-t from-black/55 via-black/5 to-transparent" />
                          </div>
                        </motion.div>
                      ))}
                    </div>

                    <aside className="flex min-h-0 flex-col justify-end pr-12 lg:pr-0">
                      <p className="font-mono text-[10px] uppercase tracking-[0.24em] mb-4" style={{ color: activeType.color }}>{activeType.label}</p>
                      <h4
                        className="font-pixel text-[clamp(2.8rem,5.5vw,5.8rem)] font-black tracking-[-0.08em] leading-none"
                        style={{
                          color: 'transparent',
                          WebkitTextFillColor: 'transparent',
                          WebkitTextStroke: `1px ${activeType.color}`,
                          backgroundImage: `linear-gradient(135deg, rgba(255,255,255,0.96), ${activeType.color}, rgba(255,255,255,0.2))`,
                          WebkitBackgroundClip: 'text',
                          backgroundClip: 'text',
                          textShadow: `0 0 24px ${activeType.color}44`,
                        }}
                      >
                        {selectedProject.title}
                      </h4>
                      <p className="mt-5 text-sm sm:text-base leading-relaxed" style={{ color: 'var(--theme-text-secondary, ' + theme.text.secondary + ')' }}>{language === 'en' ? selectedProject.descriptionEn : selectedProject.description}</p>
                      <div className="mt-6 flex flex-wrap gap-2">
                        {selectedProject.tags.map((tag) => (
                          <span key={`${selectedProject.id}-${tag}`} className="rounded-full border px-3 py-1.5 text-[10px] font-mono uppercase tracking-[0.14em]" style={{ borderColor: getBorderColor(theme, 0.18), color: 'var(--theme-text-muted, ' + theme.text.muted + ')' }}>
                            {tag}
                          </span>
                        ))}
                      </div>
                    </aside>
                  </motion.div>
                )}
                </AnimatePresence>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </section>
  );
};

// ============================================
// PROCESS SECTION (Timeline)
// ============================================
const Process: React.FC = () => {
  const { theme } = useTheme();
  const { language } = useLanguage();
  const textStyles = getTextStyles(theme);
  const textShadowLight = getTextShadowLight(theme);
  const processSteps = language === 'es' ? PROCESS_STEPS : PROCESS_STEPS_EN;

  return (
    <section
      className="relative py-24 sm:py-32 border-t"
      style={{ borderColor: getBorderColor(theme, 0.1) }}
    >
      <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-16">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-16">
          {/* Left: Header */}
          <div className="lg:col-span-5 lg:sticky lg:top-32 self-start">
            <motion.div
              initial={{ opacity: 1, y: 0 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="backdrop-blur-sm rounded-2xl p-6 sm:p-8"
              style={{
                backgroundColor: getBgColor(theme, 0.7),
              }}
            >
              <span
                className="text-xs font-mono uppercase tracking-widest mb-4 block"
                style={{
                  color: 'var(--theme-text-muted, ' + theme.text.muted + ')',
                }}
              >
                {language === 'es' ? 'Cómo funciona' : 'How it works'}
              </span>
              <h2
                className="font-pixel text-4xl sm:text-5xl font-black tracking-tight mb-6"
                style={textStyles}
              >
                {language === 'es' ? 'Un proceso claro,' : 'A streamlined,'} <br /> {language === 'es' ? 'de 4 pasos.' : '4-step process.'}
              </h2>
              <p
                className="text-lg mb-8 leading-relaxed"
                style={{
                  color: 'var(--theme-text-secondary, ' + theme.text.secondary + ')',
                  opacity: 0.8,
                }}
              >
                {language === 'es'
                  ? 'Desde la primera reunión hasta la entrega, nuestro proceso prioriza claridad, velocidad y resultados reales.'
                  : 'From discovery to deployment, our workflow prioritizes speed, clarity, and impact.'}
              </p>
              <Link
                to="/contact"
                className="inline-flex items-center gap-2 text-sm font-bold uppercase tracking-widest border-b pb-1 hover:opacity-70 transition-opacity"
                style={{ borderColor: getBorderColor(theme, 0.3) }}
              >
                {language === 'es' ? 'Agendar llamada' : 'Book a call'}
                <span className="material-symbols-outlined text-sm">arrow_forward</span>
              </Link>
            </motion.div>
          </div>

          {/* Right: Timeline */}
          <div className="lg:col-span-7 relative">
            <div
              className="absolute left-6 top-4 bottom-4 w-px"
              style={{ backgroundColor: getBorderColor(theme, 0.1) }}
            /> {/* Line */}

            <div className="space-y-16">
              {processSteps.map((step, idx) => (
                <motion.div
                  key={step.number}
                  id={`process-step-${idx + 1}`}
                  initial={{ opacity: 1, x: 0 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: idx * 0.1 }}
                  className="relative pl-28 sm:pl-32 backdrop-blur-sm rounded-xl p-6 sm:p-8"
                  style={{
                    backgroundColor: getBgColor(theme, 0.7),
                  }}
                >
                  {/* Number Circle - vertically centered relative to text block */}
                  <div
                    className="absolute left-6 top-1/2 -translate-y-1/2 w-12 h-12 sm:w-14 sm:h-14 rounded-full border flex items-center justify-center z-10 text-xl sm:text-2xl font-bold font-mono leading-none shadow-lg select-none"
                    style={{
                      backgroundColor: theme.mood === 'light' ? 'rgba(0,0,0,0.1)' : 'rgba(0,0,0,0.8)',
                      borderColor: getBorderColor(theme, 0.2),
                      color: 'var(--theme-text-primary, ' + theme.text.primary + ')',
                    }}
                  >
                    {idx + 1}
                  </div>

                  <h3
                    className="font-pixel text-2xl font-bold mb-3 ml-1 mt-0.5"
                    style={textStyles}
                  >
                    {step.title}
                  </h3>
                  <p
                    className="mb-2 font-medium"
                    style={{
                      color: 'var(--theme-text-secondary, ' + theme.text.secondary + ')',
                      opacity: 0.9,
                    }}
                  >
                    {step.description}
                  </p>
                  <p
                    className="text-sm leading-relaxed"
                    style={{
                      color: 'var(--theme-text-muted, ' + theme.text.muted + ')',
                    }}
                  >
                    {step.details}
                  </p>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};


// ============================================
// HERO SECTION
// ============================================
const Hero: React.FC = () => {
  const { theme } = useTheme();
  const { language } = useLanguage();
  const textStyles = getTextStyles(theme);
  const textShadowLight = getTextShadowLight(theme);
  const heroLine1 =
    language === 'es'
      ? 'Software a medida con estándar profesional, entregado con rapidez y criterio de negocio.'
      : 'High-quality digital products delivered fast with clear business focus.';
  const heroLine2 =
    language === 'es'
      ? 'Webs, sistemas y apps móviles con alcance claro, precio fijo por proyecto y soporte continuo.'
      : 'Websites, systems, and mobile apps with clear scope and ongoing support.';
  const leftPanelRef = useRef<HTMLDivElement>(null);
  const [leftPanelHeight, setLeftPanelHeight] = useState<number | null>(null);
  const [isDesktopWheel, setIsDesktopWheel] = useState<boolean>(
    typeof window !== 'undefined' ? window.innerWidth >= 768 : true
  );

  useEffect(() => {
    const element = leftPanelRef.current;
    if (!element) return;

    const updateHeight = () => {
      const { height } = element.getBoundingClientRect();
      setLeftPanelHeight(Math.round(height));
    };

    updateHeight();
    const observer = new ResizeObserver(updateHeight);
    observer.observe(element);
    window.addEventListener('resize', updateHeight);

    return () => {
      observer.disconnect();
      window.removeEventListener('resize', updateHeight);
    };
  }, []);

  useEffect(() => {
    const onResize = () => {
      setIsDesktopWheel(window.innerWidth >= 768);
    };
    onResize();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  return (
    <section className="relative flex items-start overflow-hidden">
      {/* Content */}
      <div className="relative z-10 w-full max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-16">
        <div className="grid grid-cols-1 lg:grid-cols-10 gap-8 lg:gap-10 items-start lg:items-center pt-6 sm:pt-8 lg:pt-8 pb-8 sm:pb-10 lg:pb-16">

          {/* Left: Typography */}
          <div className="lg:col-span-6 space-y-6 sm:space-y-8">
            <div
              ref={leftPanelRef}
              className="relative rounded-3xl border shadow-[0_30px_90px_rgba(0,0,0,0.18)] p-6 sm:p-8 lg:p-10"
              style={{
                backgroundColor: theme.mood === 'light' ? 'rgba(255,255,255,0.74)' : 'rgba(2,4,10,0.68)',
                borderColor: getBorderColor(theme, 0.2),
              }}
            >
              {!isDesktopWheel && (
                <>
                  <div className="absolute inset-0 pointer-events-none flex items-center justify-center">
                    <MahoragaPixelWheel
                      variant="shadow"
                      spin
                      wrapperClassName="opacity-100"
                      canvasClassName="block w-[364px] h-[364px]"
                    />
                  </div>

                  <div className="relative z-10">
                    <motion.div
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.8, delay: 0.2 }}
                      className="flex items-center gap-3 sm:gap-4"
                    >
                      <div
                        className="h-px w-6 sm:w-8"
                        style={{ backgroundColor: theme.mood === 'light' ? 'rgba(0,0,0,0.3)' : 'rgba(255,255,255,0.5)' }}
                      />
                      <span
                        className="text-[8px] sm:text-[9px] font-medium uppercase tracking-[0.2em] sm:tracking-[0.25em]"
                        style={{
                          color: 'var(--theme-text-secondary, ' + theme.text.secondary + ')',
                          opacity: 0.9,
                          textShadow: textShadowLight,
                        }}
                      >
                        {language === 'es' ? 'Estudio de producto digital' : 'Digital Product Studio'}
                      </span>
                    </motion.div>

                    <motion.h1
                      initial={{ opacity: 0, y: 30 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 1, delay: 0.3, ease: [0.16, 1, 0.3, 1] }}
                      className="font-pixel text-[clamp(2.2rem,9vw,4.8rem)] font-black leading-[0.85] tracking-[-0.03em] mt-3 mb-4"
                    >
                      <span style={textStyles}>Musashi</span>
                      <br />
                      <span style={{ color: 'var(--theme-text-primary, ' + theme.text.primary + ')', opacity: 0.7, textShadow: textStyles.textShadow }}>
                        Studio
                      </span>
                    </motion.h1>

                    <motion.p
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.8, delay: 0.6 }}
                      className="font-editorial text-[clamp(1.05rem,2.4vw,1.38rem)] leading-[1.45] tracking-[0.004em] select-none"
                      style={{ opacity: 0.98, color: theme.mood === 'dark' ? '#ffffff' : '#111827', textShadow: textShadowLight }}
                    >
                      <span className="block">{heroLine1}</span>
                      <span className="block opacity-90">{heroLine2}</span>
                    </motion.p>

                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.8, delay: 0.8 }}
                      className="flex items-center pt-7"
                    >
                      <a
                        href="#work"
                        className="group inline-flex items-center gap-3 text-sm font-semibold uppercase tracking-[0.15em] pb-2 border-b transition-colors"
                        style={{
                          color: 'var(--theme-text-primary, ' + theme.text.primary + ')',
                          borderColor: theme.mood === 'light' ? 'rgba(0,0,0,0.3)' : 'rgba(255,255,255,0.3)',
                          textShadow: textShadowLight,
                        }}
                      >
                        {language === 'es' ? 'Ver proyectos' : 'View work'}
                        <span className="material-symbols-outlined text-base transition-transform group-hover:translate-x-1">
                          arrow_forward
                        </span>
                      </a>
                    </motion.div>
                  </div>
                </>
              )}

              {isDesktopWheel && (
                <div className="relative z-10 h-[clamp(400px,42vw,550px)] grid grid-cols-[60%_40%] grid-rows-[auto_auto_auto]">
                  <motion.div
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.8, delay: 0.2 }}
                    className="col-span-2 row-start-1 row-end-2 flex items-center gap-3 -mb-2"
                  >
                    <div
                      className="h-px w-8"
                      style={{ backgroundColor: theme.mood === 'light' ? 'rgba(0,0,0,0.3)' : 'rgba(255,255,255,0.5)' }}
                    />
                    <span
                      className="text-[11px] font-medium uppercase tracking-[0.24em]"
                      style={{
                        color: 'var(--theme-text-secondary, ' + theme.text.secondary + ')',
                        opacity: 0.9,
                        textShadow: textShadowLight,
                      }}
                    >
                      {language === 'es' ? 'Estudio de producto digital' : 'Digital Product Studio'}
                    </span>
                  </motion.div>

                  <motion.h1
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 1, delay: 0.3, ease: [0.16, 1, 0.3, 1] }}
                    className="row-start-2 row-end-3 col-start-1 col-end-2 self-start font-pixel text-[clamp(3.2rem,7.2vw,8.6rem)] font-black leading-[0.9] tracking-[-0.035em]"
                  >
                    <span style={textStyles}>Musashi</span>
                    <br />
                    <span style={{ color: 'var(--theme-text-primary, ' + theme.text.primary + ')', opacity: 0.7, textShadow: textStyles.textShadow }}>
                      Studio
                    </span>
                  </motion.h1>

                  <motion.div
                    initial={{ opacity: 0, scale: 0.8, rotate: -8 }}
                    animate={{ opacity: 1, scale: 1, rotate: 0 }}
                    transition={{ duration: 0.6, delay: 0.75, ease: [0.16, 1, 0.3, 1] }}
                    className="row-start-2 row-end-3 col-start-2 col-end-3 flex items-start justify-center self-start -mt-[clamp(30px,3.9vw,48px)]"
                  >
                    <MahoragaPixelWheel canvasClassName="block w-[clamp(190px,20vw,320px)] h-[clamp(190px,20vw,320px)]" />
                  </motion.div>

                  <div className="row-start-3 row-end-4 col-span-2 flex flex-col justify-start gap-1 mt-0 self-start">
                    <motion.p
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.8, delay: 0.6 }}
                      className="font-editorial text-[clamp(1.02rem,1.42vw,1.55rem)] leading-[1.38] tracking-[0.003em] select-none max-w-[60ch]"
                      style={{ opacity: 0.98, color: theme.mood === 'dark' ? '#ffffff' : '#111827', textShadow: textShadowLight }}
                    >
                      <span className="block">{heroLine1}</span>
                      <span className="block opacity-90">{heroLine2}</span>
                    </motion.p>

                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.8, delay: 0.8 }}
                      className="flex items-center pt-5"
                    >
                      <a
                        href="#work"
                        className="group inline-flex items-center gap-3 text-sm font-semibold uppercase tracking-[0.15em] pb-2 border-b transition-colors"
                        style={{
                          color: 'var(--theme-text-primary, ' + theme.text.primary + ')',
                          borderColor: theme.mood === 'light' ? 'rgba(0,0,0,0.3)' : 'rgba(255,255,255,0.3)',
                          textShadow: textShadowLight,
                        }}
                      >
                        {language === 'es' ? 'Ver proyectos' : 'View work'}
                        <span className="material-symbols-outlined text-base transition-transform group-hover:translate-x-1">
                          arrow_forward
                        </span>
                      </a>
                    </motion.div>
                  </div>
                </div>
              )}
            </div>
            {/* Capabilities - REMOVED, moving to dedicated section */}
            {/* <motion.div ... > ... </motion.div> */}
          </div>

          {/* Right: Scrolling Gallery */}
          <div
            className="lg:col-span-4 h-[360px] sm:h-[420px] md:h-[480px] lg:h-[520px] relative min-h-0"
            style={{
              height: typeof window !== 'undefined' && window.innerWidth >= 1024 && leftPanelHeight
                ? `${Math.round(leftPanelHeight * 1.15)}px`
                : undefined,
            }}
          >
            <ScrollingGallery />
          </div>
        </div>
      </div>

    </section>
  );
};

// ============================================
// PROJECT CARD COMPONENT
// ============================================
interface ProjectCardProps {
  project: Project;
  index: number;
  featured?: boolean;
}

const ProjectCard: React.FC<ProjectCardProps> = ({ project, index, featured }) => {
  const { theme } = useTheme();
  const { language } = useLanguage();
  const textShadowLight = getTextShadowLight(theme);
  const textStyles = getTextStyles(theme);
  const [isHovered, setIsHovered] = useState(false);

  return (
    <div
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      className={`group relative ${featured ? 'col-span-1 sm:col-span-2 row-span-1 sm:row-span-2' : ''}`}
    >
      <Link
        to={`/works/${project.id}`}
        className="block relative h-full rounded-2xl overflow-hidden border transition-all duration-500 ease-out"
        style={{
          backgroundColor: getBgColor(theme, 0.2),
          borderColor: getBorderColor(theme, 0.05),
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.borderColor = getBorderColor(theme, 0.2);
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.borderColor = getBorderColor(theme, 0.05);
        }}
      >
        <ProjectMiniatureMedia
          src={project.image}
          fallbackSrc="/projects/lumbra/portada.png"
          alt={project.title}
          title={project.title}
          subtitle={language === 'en' ? project.subtitleEn : project.subtitle}
          imageScale={isHovered ? 1.08 : 1}
          wrapperClassName={`relative overflow-hidden ${featured ? 'aspect-[4/3] sm:aspect-[16/10]' : 'aspect-[4/3]'}`}
          titleClassName={`font-pixel font-bold mb-1.5 sm:mb-2 transition-colors ${featured ? 'text-xl sm:text-3xl md:text-4xl' : 'text-lg sm:text-xl md:text-2xl'}`}
          titleStyle={{
            ...textStyles,
            color: isHovered ? project.color : 'var(--theme-text-primary, ' + theme.text.primary + ')',
          }}
          subtitleClassName="text-xs sm:text-sm"
          subtitleStyle={{
            color: 'var(--theme-text-secondary, ' + theme.text.secondary + ')',
            opacity: 0.9,
            textShadow: textShadowLight,
          }}
        >
          <div
            className="flex flex-wrap gap-1.5 sm:gap-2 mt-3 sm:mt-4"
            style={{
              opacity: isHovered ? 1 : 0,
              transform: isHovered ? 'translateY(0)' : 'translateY(8px)',
              transition: 'opacity 220ms ease, transform 220ms ease',
            }}
          >
            {project.tags.map((tag) => (
              <span
                key={tag}
                className="text-[9px] sm:text-[10px] font-mono px-2 py-1"
                style={{
                  color: 'var(--theme-text-secondary, ' + theme.text.secondary + ')',
                  opacity: 0.9,
                  backgroundColor: getBgColor(theme, 0.4),
                }}
              >
                {tag}
              </span>
            ))}
          </div>
        </ProjectMiniatureMedia>

        <div
          className="p-4 sm:p-5 min-h-[132px] transition-colors duration-500 ease-out"
          style={{
            backgroundColor: getBgColor(theme, isHovered ? 0.55 : 0.35),
          }}
        >
          <div className="flex items-center justify-between gap-3 mb-3">
            <span
              className="text-[10px] font-mono uppercase tracking-[0.18em]"
              style={{ color: 'var(--theme-text-muted, ' + theme.text.muted + ')' }}
            >
              {project.category}
            </span>
            <span
              className="text-[10px] font-mono"
              style={{ color: 'var(--theme-text-muted, ' + theme.text.muted + ')' }}
            >
              {project.year}
            </span>
          </div>
          <p
            className="text-sm leading-relaxed transition-opacity duration-500"
            style={{ color: 'var(--theme-text-secondary, ' + theme.text.secondary + ')', opacity: isHovered ? 1 : 0.86 }}
          >
            {language === 'en' ? project.descriptionEn : project.description}
          </p>
          <div
            className="mt-4 flex items-center gap-2 text-[10px] font-semibold uppercase tracking-[0.16em] transition-all duration-500"
            style={{
              color: project.color,
              opacity: isHovered ? 1 : 0,
              transform: isHovered ? 'translateY(0)' : 'translateY(8px)',
            }}
          >
            <span>{language === 'es' ? 'Ver más del proyecto' : 'See project details'}</span>
            <span className="material-symbols-outlined text-sm">arrow_forward</span>
          </div>
        </div>

        {/* Lightweight hover tint */}
        <div
          className="absolute inset-0 pointer-events-none transition-opacity duration-300"
          style={{ backgroundColor: project.color, opacity: isHovered ? 0.12 : 0 }}
        />

        {/* Corner accent */}
        <div
          className="absolute top-3 sm:top-4 right-3 sm:right-4 w-2 h-2"
          style={{
            backgroundColor: isHovered ? project.color : 'rgba(255,255,255,0.3)',
            transition: 'background-color 200ms ease',
          }}
        />

        {/* Arrow indicator */}
        <div
          className="absolute top-4 sm:top-6 right-4 sm:right-6"
          style={{
            opacity: isHovered ? 1 : 0,
            transform: isHovered ? 'translateX(0)' : 'translateX(-8px)',
            transition: 'opacity 220ms ease, transform 220ms ease',
          }}
        >
          <span
            className="material-symbols-outlined text-lg sm:text-xl"
            style={{
              color: 'var(--theme-text-primary, ' + theme.text.primary + ')',
              textShadow: textShadowLight,
            }}
          >
            arrow_outward
          </span>
        </div>
      </Link>
    </div>
  );
};

// ============================================
// PORTFOLIO SECTION
// ============================================
const Portfolio: React.FC = () => {
  const { theme } = useTheme();
  const { language } = useLanguage();
  const textStyles = getTextStyles(theme);
  const textShadowLight = getTextShadowLight(theme);
  const [activeFilter, setActiveFilter] = useState<ProjectCategory>('all');

  const filters: { label: string; value: ProjectCategory }[] = [
    { label: language === 'es' ? 'Todos' : 'All', value: 'all' },
    { label: language === 'es' ? 'Productos' : 'Products', value: 'product' },
    { label: 'Marketplaces', value: 'marketplace' },
    { label: language === 'es' ? 'Sitios web' : 'Websites', value: 'website' },
    { label: 'Landings', value: 'landing' },
    { label: 'AI', value: 'ai' },
  ];

  const categoryCopy: Record<string, { title: string; description: string }> = language === 'es'
    ? {
      product: { title: 'Productos y sistemas internos', description: 'SaaS, ERP, dashboards y herramientas operativas donde el valor está en el flujo completo.' },
      marketplace: { title: 'Marketplaces', description: 'Plataformas multirol con usuarios, proveedores, backoffice, pagos y operación comercial.' },
      website: { title: 'Sitios web', description: 'Webs institucionales y de marca enfocadas en claridad, confianza y conversión.' },
      landing: { title: 'Landings', description: 'Páginas directas para validar, vender o explicar una propuesta con velocidad.' },
      ai: { title: 'AI y experiencias avanzadas', description: 'Interfaces, automatizaciones y experiencias visuales potenciadas por IA.' },
      experimental: { title: 'Experimental', description: 'Exploraciones visuales, prototipos y pruebas técnicas.' },
    }
    : {
      product: { title: 'Products and internal systems', description: 'SaaS, ERP, dashboards, and operational tools where value lives in the complete workflow.' },
      marketplace: { title: 'Marketplaces', description: 'Multi-role platforms with users, providers, backoffice, payments, and commercial operations.' },
      website: { title: 'Websites', description: 'Institutional and brand websites focused on clarity, trust, and conversion.' },
      landing: { title: 'Landings', description: 'Focused pages to validate, sell, or explain an offer quickly.' },
      ai: { title: 'AI and advanced experiences', description: 'Interfaces, automations, and visual experiences powered by AI.' },
      experimental: { title: 'Experimental', description: 'Visual explorations, prototypes, and technical tests.' },
    };

  const categoryOrder: ProjectCategory[] = ['product', 'marketplace', 'website', 'landing', 'ai', 'experimental'];
  const visibleGroups = categoryOrder
    .map((category) => ({
      category,
      projects: PROJECTS.filter((project) => activeFilter === 'all' ? project.category === category : project.category === activeFilter),
    }))
    .filter((group) => group.projects.length > 0);

  return (
    <section id="work" className="relative py-16 sm:py-24 md:py-32">
      <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-16">
        {/* Section header */}
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 sm:gap-8 mb-10 sm:mb-16">
          <motion.div
            initial={{ opacity: 1, y: 0 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="backdrop-blur-sm rounded-2xl p-6 sm:p-8"
            style={{ backgroundColor: getBgColor(theme, 0.7) }}
          >
            <span
              className="text-[11px] uppercase tracking-[0.28em] mb-4 block"
              style={{ color: 'var(--theme-text-muted, ' + theme.text.muted + ')' }}
            >
              {PROJECTS.length} {language === 'es' ? 'proyectos reales' : 'real projects'}
            </span>
            <h2
              className="font-pixel text-3xl sm:text-4xl md:text-6xl font-black tracking-tight mb-4"
              style={textStyles}
            >
              {language === 'es' ? 'Primero, los proyectos.' : 'Projects first.'}
            </h2>
            <p
              className="text-base sm:text-lg max-w-2xl leading-relaxed"
              style={{ color: 'var(--theme-text-secondary, ' + theme.text.secondary + ')', opacity: 0.84 }}
            >
              {language === 'es'
                ? 'Cada visitante debe entender rápido qué hemos construido: sistemas, marketplaces, sitios web, landings y experiencias con IA, separados por categoría.'
                : 'Every visitor should quickly understand what we have built: systems, marketplaces, websites, landings, and AI experiences, separated by category.'}
            </p>
          </motion.div>

          {/* Filter tabs */}
          <motion.div
            initial={{ opacity: 1, y: 0 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="flex flex-wrap gap-1.5 sm:gap-2"
          >
            {filters.map((filter) => (
              <button
                key={filter.value}
                onClick={() => setActiveFilter(filter.value)}
                className="text-[10px] sm:text-[11px] uppercase tracking-[0.1em] sm:tracking-[0.15em] px-3 sm:px-4 py-1.5 sm:py-2 transition-all"
                style={{
                  textShadow: textShadowLight,
                  color: activeFilter === filter.value
                    ? 'var(--theme-text-primary, ' + theme.text.primary + ')'
                    : 'var(--theme-text-secondary, ' + theme.text.secondary + ')',
                  backgroundColor: activeFilter === filter.value
                    ? theme.mood === 'light'
                      ? 'rgba(17,24,39,0.12)'
                      : 'rgba(255,255,255,0.15)'
                    : 'transparent',
                  border: activeFilter === filter.value
                    ? `1px solid ${theme.mood === 'light' ? 'rgba(17,24,39,0.24)' : 'rgba(255,255,255,0.22)'}`
                    : `1px solid ${theme.mood === 'light' ? 'rgba(17,24,39,0.08)' : 'rgba(255,255,255,0.08)'}`,
                }}
              >
                {filter.label}
              </button>
            ))}
          </motion.div>
        </div>

        {/* Projects grouped by category */}
        <div key={activeFilter} className="space-y-14 sm:space-y-20">
          {visibleGroups.map((group) => (
            <div key={group.category} className="rounded-3xl border p-4 sm:p-6 lg:p-8" style={{ borderColor: getBorderColor(theme, 0.12), backgroundColor: getBgColor(theme, 0.42) }}>
              <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4 mb-6 sm:mb-8">
                <div>
                  <span
                    className="text-[10px] font-mono uppercase tracking-[0.22em] mb-3 block"
                    style={{ color: 'var(--theme-text-muted, ' + theme.text.muted + ')' }}
                  >
                    {group.projects.length} {language === 'es' ? 'proyecto' : 'project'}{group.projects.length !== 1 ? 's' : ''}
                  </span>
                  <h3 className="font-pixel text-2xl sm:text-3xl font-black" style={textStyles}>
                    {categoryCopy[group.category]?.title ?? group.category}
                  </h3>
                </div>
                <p
                  className="text-sm sm:text-base max-w-xl leading-relaxed"
                  style={{ color: 'var(--theme-text-secondary, ' + theme.text.secondary + ')', opacity: 0.82 }}
                >
                  {categoryCopy[group.category]?.description}
                </p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-5 sm:gap-6">
                {group.projects.map((project, idx) => (
                  <ProjectCard
                    key={project.id}
                    project={project}
                    index={idx}
                  />
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* View all link */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          className="flex justify-center mt-10 sm:mt-16"
        >
          <Link
            to="/works"
            className="group inline-flex items-center gap-3 text-sm uppercase tracking-[0.15em] transition-colors"
            style={{ textShadow: textShadowLight, color: 'var(--theme-text-secondary, ' + theme.text.secondary + ')' }}
          >
            {language === 'es' ? 'Ver todos los proyectos' : 'View all projects'}
            <span className="material-symbols-outlined text-base transition-transform group-hover:translate-x-1">
              arrow_forward
            </span>
          </Link>
        </motion.div>
      </div>
    </section>
  );
};

// ============================================
// COMPARISON SECTION
// ============================================
const Comparison: React.FC = () => {
  const { theme } = useTheme();
  const { language } = useLanguage();
  const textStyles = getTextStyles(theme);

  const serviceInclusions = language === 'es'
    ? [
      { label: 'Alcance', value: 'Definici\u00F3n clara de objetivos, entregables y tiempos.' },
      { label: 'Dise\u00F1o de producto', value: 'UX/UI orientado a uso real, claridad y conversi\u00F3n.' },
      { label: 'Desarrollo', value: 'Implementaci\u00F3n web, sistema interno o app m\u00F3vil seg\u00FAn el caso.' },
      { label: 'Entregas', value: 'Avances semanales con revisi\u00F3n conjunta y decisiones r\u00E1pidas.' },
      { label: 'Calidad', value: 'C\u00F3digo mantenible, estructura limpia y documentaci\u00F3n base.' },
      { label: 'Soporte', value: 'Seguimiento t\u00E9cnico post-lanzamiento para estabilidad.' },
      { label: 'Nuevos cambios', value: 'Se cotizan por alcance adicional, sin costos ocultos.' }
    ]
    : [
      { label: 'Scope', value: 'Clear definition of goals, deliverables, and timeline.' },
      { label: 'Product design', value: 'UX/UI focused on real usage, clarity, and conversion.' },
      { label: 'Development', value: 'Website, internal system, or mobile app implementation based on your needs.' },
      { label: 'Delivery', value: 'Weekly progress with collaborative reviews and fast decisions.' },
      { label: 'Quality', value: 'Maintainable code, clean structure, and baseline documentation.' },
      { label: 'Support', value: 'Post-launch technical follow-up for stability.' },
      { label: 'New requests', value: 'Quoted as additional scope with transparent costs.' }
    ];

  return (
    <section id="inclusions" className="relative py-24 sm:py-32">
      <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-16">
        <div className="max-w-[1120px] mx-auto">
          {/* Mobile: cards */}
          <div className="lg:hidden space-y-4 rounded-2xl border p-4 sm:p-5 backdrop-blur-sm" style={{ backgroundColor: getBgColor(theme, 0.35), borderColor: getBorderColor(theme, 0.12) }}>
            {serviceInclusions.map((row, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.05 }}
                className="rounded-xl border p-5"
                style={{
                  backgroundColor: getBgColor(theme, 0.45),
                  borderColor: getBorderColor(theme, 0.15),
                }}
              >
                <h4 className="text-base font-semibold mb-3 pb-3 border-b" style={{ ...textStyles, borderColor: getBorderColor(theme, 0.15) }}>
                  {row.label}
                </h4>
                <p className="text-sm leading-relaxed" style={{ color: 'var(--theme-text-secondary, ' + theme.text.secondary + ')', opacity: 0.9 }}>
                  {row.value}
                </p>
              </motion.div>
            ))}
          </div>

          {/* Desktop: table */}
          <div className="hidden lg:block rounded-2xl border p-5 xl:p-6 backdrop-blur-sm" style={{ backgroundColor: getBgColor(theme, 0.35), borderColor: getBorderColor(theme, 0.12) }}>
            <div className="overflow-x-auto">
              <table className="w-full min-w-[800px] text-left border-collapse">
                <thead>
                  <tr style={{ borderColor: getBorderColor(theme, 0.1) }} className="border-b">
                    <th className="py-6 px-4 font-normal uppercase tracking-widest text-sm" style={{ color: 'var(--theme-text-muted, ' + theme.text.muted + ')' }}>
                      {language === 'es' ? 'Aspecto' : 'Area'}
                    </th>
                    <th className="py-6 px-4 font-normal uppercase tracking-widest text-sm" style={{ color: 'var(--theme-text-muted, ' + theme.text.muted + ')' }}>
                      {language === 'es' ? 'Qu\u00E9 recibes' : 'What you get'}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {serviceInclusions.map((row, idx) => (
                    <tr
                      key={idx}
                      className="border-b transition-colors"
                      style={{ borderColor: getBorderColor(theme, 0.05) }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = theme.mood === 'light' ? 'rgba(0,0,0,0.05)' : 'rgba(255,255,255,0.05)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = 'transparent';
                      }}
                    >
                      <td className="py-6 px-4 font-medium" style={textStyles}>
                        {row.label}
                      </td>
                      <td className="py-6 px-4" style={{ color: 'var(--theme-text-secondary, ' + theme.text.secondary + ')', opacity: 0.9 }}>
                        {row.value}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
// ============================================
// FAQ SECTION
// ============================================
const FAQ: React.FC = () => {
  const { theme } = useTheme();
  const { language } = useLanguage();
  const textStyles = getTextStyles(theme);
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const faqs = language === 'es' ? FAQS : FAQS_EN;
  const calendarUrl = 'https://calendar.google.com/calendar/appointments/schedules/AcZssZ1ZNd-9AnZBZepZsNeM32KTjfcbNb4Od_xsEUW1DyDGohjS-JDtaPfA0WRCAFlixFkWQHXpcexX?gv=true';
  const contactEmail = 'hello@musashi.studio';

  const onSubmitMessage = (event: React.FormEvent) => {
    event.preventDefault();
    const safeName = name.trim();
    const safeEmail = email.trim();
    const safeMessage = message.trim();
    if (!safeName || !safeEmail || !safeMessage) return;

    const subject = encodeURIComponent(`Nuevo mensaje web - ${safeName}`);
    const body = encodeURIComponent(
      `Nombre: ${safeName}\nCorreo: ${safeEmail}\n\nMensaje:\n${safeMessage}`
    );
    window.location.href = `mailto:${contactEmail}?subject=${subject}&body=${body}`;
  };

  return (
    <section
      id="faq"
      className="relative py-24 sm:py-32 border-t"
      style={{ borderColor: getBorderColor(theme, 0.1) }}
    >
      <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-16">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-10 items-start">
          <div className="lg:col-span-7">
            <div className="mb-10">
              <h2 className="text-4xl font-black mb-4">{language === 'es' ? 'Preguntas frecuentes' : 'FAQs'}</h2>
              <p style={{ color: 'var(--theme-text-secondary, ' + theme.text.secondary + ')' }}>
                {language === 'es' ? 'Dudas comunes sobre cómo trabajamos.' : 'Common questions about how we work.'}
              </p>
            </div>

            <div className="space-y-4">
              {faqs.map((faq, idx) => (
                <div
                  key={idx}
                  className="border rounded-lg overflow-hidden backdrop-blur-sm"
                  style={{
                    borderColor: getBorderColor(theme, 0.1),
                    backgroundColor: getBgColor(theme, 0.4),
                  }}
                >
                  <button
                    onClick={() => setOpenIndex(openIndex === idx ? null : idx)}
                    className="w-full flex items-center justify-between p-6 text-left transition-colors"
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = theme.mood === 'light' ? 'rgba(0,0,0,0.05)' : 'rgba(255,255,255,0.05)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = 'transparent';
                    }}
                  >
                    <span className="font-bold text-lg">{faq.question}</span>
                    <span className="material-symbols-outlined transition-transform duration-300" style={{ transform: openIndex === idx ? 'rotate(180deg)' : 'rotate(0deg)' }}>
                      expand_more
                    </span>
                  </button>
                  <AnimatePresence>
                    {openIndex === idx && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        className="overflow-hidden"
                      >
                        <div
                          className="p-6 pt-0 leading-relaxed"
                          style={{
                            color: 'var(--theme-text-secondary, ' + theme.text.secondary + ')',
                            opacity: 0.9,
                          }}
                        >
                          {faq.answer}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              ))}
            </div>
          </div>

          <div className="lg:col-span-5">
            <div
              className="rounded-2xl border p-6 sm:p-8 backdrop-blur-sm lg:sticky lg:top-28"
              style={{
                backgroundColor: getBgColor(theme, 0.5),
                borderColor: getBorderColor(theme, 0.14),
              }}
            >
              <h3 className="text-2xl font-black mb-2" style={textStyles}>
                {language === 'es' ? 'Cuéntame tu proyecto' : 'Tell me about your project'}
              </h3>
              <p
                className="text-sm mb-6"
                style={{ color: 'var(--theme-text-secondary, ' + theme.text.secondary + ')', opacity: 0.9 }}
              >
                {language === 'es'
                  ? 'Déjame tus datos y te respondo con una propuesta clara.'
                  : 'Leave your details and I will get back with a clear proposal.'}
              </p>

              <form className="space-y-4" onSubmit={onSubmitMessage}>
                <div>
                  <label htmlFor="project-name" className="text-xs uppercase tracking-wider block mb-2" style={{ color: 'var(--theme-text-muted, ' + theme.text.muted + ')' }}>
                    {language === 'es' ? 'Nombre' : 'Name'}
                  </label>
                  <input
                    id="project-name"
                    name="name"
                    autoComplete="name"
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    required
                    className="w-full rounded-lg border px-4 py-3 bg-transparent"
                    style={{ borderColor: getBorderColor(theme, 0.2), color: 'var(--theme-text-primary, ' + theme.text.primary + ')' }}
                  />
                </div>

                <div>
                  <label htmlFor="project-email" className="text-xs uppercase tracking-wider block mb-2" style={{ color: 'var(--theme-text-muted, ' + theme.text.muted + ')' }}>
                    {language === 'es' ? 'Correo' : 'Email'}
                  </label>
                  <input
                    id="project-email"
                    name="email"
                    autoComplete="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className="w-full rounded-lg border px-4 py-3 bg-transparent"
                    style={{ borderColor: getBorderColor(theme, 0.2), color: 'var(--theme-text-primary, ' + theme.text.primary + ')' }}
                  />
                </div>

                <div>
                  <label htmlFor="project-message" className="text-xs uppercase tracking-wider block mb-2" style={{ color: 'var(--theme-text-muted, ' + theme.text.muted + ')' }}>
                    {language === 'es' ? 'Mensaje' : 'Message'}
                  </label>
                  <textarea
                    id="project-message"
                    name="message"
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    required
                    rows={5}
                    className="w-full rounded-lg border px-4 py-3 bg-transparent resize-none"
                    style={{ borderColor: getBorderColor(theme, 0.2), color: 'var(--theme-text-primary, ' + theme.text.primary + ')' }}
                  />
                </div>

                <div className="pt-2 flex flex-col sm:flex-row gap-3">
                  <button
                    type="submit"
                    className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full text-sm font-bold uppercase tracking-wider"
                    style={{ backgroundColor: theme.mood === 'light' ? '#0A0A0A' : '#FFFFFF', color: theme.mood === 'light' ? '#FFFFFF' : '#0A0A0A' }}
                  >
                    {language === 'es' ? 'Enviar mensaje' : 'Send message'}
                    <span className="material-symbols-outlined text-base">send</span>
                  </button>

                  <a
                    href={calendarUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full text-sm font-bold uppercase tracking-wider border"
                    style={{ borderColor: getBorderColor(theme, 0.25), color: 'var(--theme-text-primary, ' + theme.text.primary + ')' }}
                  >
                    {language === 'es' ? 'Agendar reunión' : 'Book a meeting'}
                    <span className="material-symbols-outlined text-base">event</span>
                  </a>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
// ============================================
// HOME PAGE
// ============================================
const Home: React.FC = () => {
  return (
    <main>

      <Hero />
      <Services />
      <Process />
      <Portfolio />
      <FAQ />
    </main>
  );
};

export default Home;
